import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

// Types matching backend LLM usage stats
export interface LLMUsageDataPoint {
  date: string; // ISO date string
  provider_name: string;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  avg_latency_ms: number;
}

export interface LLMProviderSummary {
  provider_name: string;
  display_name: string;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  avg_latency_ms: number;
}

export interface LLMUsageChartProps {
  usageData: LLMUsageDataPoint[];
  providerSummaries: LLMProviderSummary[];
  periodDays?: number;
}

// Colores para cada provider (matching card)
const PROVIDER_COLORS: Record<string, string> = {
  openai: "#10b981", // emerald-500
  anthropic: "#a855f7", // purple-500
  google: "#3b82f6", // blue-500
  azure_openai: "#0ea5e9", // sky-500
  default: "#6b7280", // gray-500
};

function getProviderColor(providerName: string): string {
  return PROVIDER_COLORS[providerName] || PROVIDER_COLORS.default;
}

export function LLMUsageChart({
  usageData,
  providerSummaries,
  periodDays = 30,
}: LLMUsageChartProps) {
  // Agrupar datos por fecha para el BarChart
  const requestsByDateData = useMemo(() => {
    const dateMap = new Map<string, Record<string, number>>();

    usageData.forEach((item) => {
      const dateKey = new Date(item.date).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
      });

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {});
      }

      const dayData = dateMap.get(dateKey)!;
      dayData[item.provider_name] =
        (dayData[item.provider_name] || 0) + item.total_requests;
    });

    // Convertir a array para Recharts
    return Array.from(dateMap.entries())
      .map(([date, providers]) => ({
        date,
        ...providers,
      }))
      .slice(-periodDays);
  }, [usageData, periodDays]);

  // Datos para PieChart de distribución de costos
  const costDistributionData = useMemo(() => {
    return providerSummaries
      .filter((p) => p.total_cost_usd > 0)
      .map((p) => ({
        name: p.display_name,
        value: Number(p.total_cost_usd),
        provider_name: p.provider_name,
      }));
  }, [providerSummaries]);

  // Obtener lista única de providers para las barras
  const uniqueProviders = useMemo(() => {
    const providers = new Set<string>();
    usageData.forEach((item) => providers.add(item.provider_name));
    return Array.from(providers);
  }, [usageData]);

  if (usageData.length === 0 && providerSummaries.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <p className="text-gray-500">
          No hay datos de uso disponibles para el período seleccionado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Requests por día (BarChart) */}
      {requestsByDateData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Requests por día (últimos {periodDays} días)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={requestsByDateData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {uniqueProviders.map((provider) => (
                <Bar
                  key={provider}
                  dataKey={provider}
                  fill={getProviderColor(provider)}
                  name={provider}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Distribución de costos por provider (PieChart) */}
      {costDistributionData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Distribución de costos por proveedor
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costDistributionData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) =>
                  `${entry.name}: $${entry.value.toFixed(2)}`
                }
              >
                {costDistributionData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getProviderColor(entry.provider_name)}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number | undefined) =>
                  value !== undefined ? `$${value.toFixed(2)}` : "$0.00"
                }
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabla resumen */}
      {providerSummaries.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Resumen por proveedor
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Proveedor
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">
                    Total Requests
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">
                    Total Tokens
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">
                    Costo Total
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">
                    Latencia Promedio
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {providerSummaries.map((summary) => (
                  <tr
                    key={summary.provider_name}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-800 font-medium">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: getProviderColor(
                              summary.provider_name
                            ),
                          }}
                        />
                        {summary.display_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {summary.total_requests.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {summary.total_tokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-medium">
                      ${Number(summary.total_cost_usd).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {summary.avg_latency_ms.toFixed(0)} ms
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Total row */}
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-semibold">
                  <td className="px-4 py-3 text-gray-800">Total</td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    {providerSummaries
                      .reduce((sum, p) => sum + p.total_requests, 0)
                      .toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    {providerSummaries
                      .reduce((sum, p) => sum + p.total_tokens, 0)
                      .toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    $
                    {providerSummaries
                      .reduce((sum, p) => sum + Number(p.total_cost_usd), 0)
                      .toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    {providerSummaries.length > 0
                      ? (
                          providerSummaries.reduce(
                            (sum, p) => sum + p.avg_latency_ms,
                            0
                          ) / providerSummaries.length
                        ).toFixed(0)
                      : 0}{" "}
                    ms
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
